import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from 'react-router-dom';

export function initSentry(): void {
  // Only initialize if DSN is configured
  if (!import.meta.env.VITE_SENTRY_DSN) {
    console.warn('[Sentry] No DSN configured, skipping initialization');
    return;
  }

  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    release: `pistachiorama-ui@${typeof __COMMIT_HASH__ !== 'undefined' ? __COMMIT_HASH__ : 'dev'}`,

    integrations: [
      // Use React Router v6 integration for parameterized route names
      // (e.g., /stream/:id instead of /stream/abc123)
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration({
        // Privacy: Mask all text by default (unmask specific elements with data-sentry-unmask)
        maskAllText: true,
        blockAllMedia: false,
      }),
    ],

    // Distributed tracing - connect frontend to backend via auto-instrumented sentry-trace headers
    tracePropagationTargets: [
      'localhost',
      /^https:\/\/api\.pistachiorama\.ai/,
      /^https:\/\/.*\.pistachiorama\.workers\.dev/,
    ],

    // Semantic sampling - prioritize by business value
    tracesSampler: (samplingContext) => {
      const name = samplingContext.name || '';
      const defaultRate = parseFloat(
        import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || '0.1'
      );

      // Drop noise
      if (name.includes('healthcheck') || name.includes('heartbeat')) {
        return 0;
      }

      // Always capture critical paths
      if (
        name.includes('/chat') ||
        name.includes('/stream') ||
        name.includes('/auth')
      ) {
        return 1.0;
      }

      // Default sample rate (configurable via env)
      return defaultRate;
    },

    // Session replay - buffer-on-error only
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
  });
}

// Re-export Sentry for use in components
export { Sentry };
